import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatPrice, formatDate } from '../utils/formatters';
import type {
  SubscriptionDetailsResponse,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  AgentSubscription,
  StripeProductsRequest,
  StripeProductsResponse
} from '../types/subscription';

/**
 * Get available agent subscription products from Stripe
 * @returns Available subscription products
 */
export const getAvailableProducts = async (): Promise<StripeProductsResponse> => {
  try {
    const getProductsFunction = httpsCallable<StripeProductsRequest, StripeProductsResponse>(
      functions,
      'getStripeProducts'
    );
    const result = await getProductsFunction({ type: 'agent_subscription' });
    return result.data;
  } catch (error) {
    console.error('Error fetching subscription products:', error);
    // Rethrow the original error. Wrapping it in a new Error discarded the
    // Firebase `code` (which ErrorService.getUserFriendlyMessage dispatches on)
    // and the server's HttpsError message.
    throw error instanceof Error ? error : new Error('Failed to fetch subscription products');
  }
};

/**
 * Create a subscription checkout session
 * @param priceId - Stripe price ID
 * @returns Checkout session details
 */
export const createSubscriptionCheckout = async (
  priceId: string
): Promise<CreateSubscriptionResponse> => {
  try {
    const createSubscriptionFunction = httpsCallable<
      CreateSubscriptionRequest,
      CreateSubscriptionResponse
    >(functions, 'createAgentSubscription');

    const result = await createSubscriptionFunction({ priceId });
    return result.data;
  } catch (error) {
    console.error('Error creating subscription checkout:', error);
    throw error instanceof Error ? error : new Error('Failed to create subscription checkout');
  }
};

/**
 * Get agent's subscription details and customer portal URL
 * @returns Subscription details and portal URL
 */
export const getSubscriptionDetails = async (): Promise<SubscriptionDetailsResponse> => {
  try {
    const getDetailsFunction = httpsCallable<void, SubscriptionDetailsResponse>(
      functions,
      'getAgentSubscriptionDetails'
    );
    const result = await getDetailsFunction();
    return result.data;
  } catch (error) {
    console.error('Error fetching subscription details:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch subscription details');
  }
};

/**
 * Result of a subscription check.
 *
 * `determined: false` means the lookup failed - it is NOT the same as "no
 * subscription". Returning a bare `false` on any error made a paying agent look
 * unsubscribed after a network blip, so callers must handle the third state.
 */
export type SubscriptionCheck =
  | { determined: true; active: boolean }
  | { determined: false; active: false; error: unknown };

/**
 * Check if agent has an active (or trialing) subscription.
 */
export const hasActiveSubscription = async (): Promise<SubscriptionCheck> => {
  try {
    const details = await getSubscriptionDetails();
    const active =
      details.subscription !== null &&
      (details.subscription.status === 'active' ||
        details.subscription.status === 'trialing');
    return { determined: true, active };
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return { determined: false, active: false, error };
  }
};

// Money/date formatting lives in one place - see utils/formatters.ts.
// Re-exported so existing `from '../../services/subscriptionService'` imports
// keep working.
export { formatPrice, formatDate };

/**
 * Get status badge configuration
 * @param status - Subscription status
 * @returns Badge configuration
 */
export const getStatusBadge = (
  status: AgentSubscription['status']
): { label: string; severity: 'success' | 'warning' | 'danger' | 'info' } => {
  switch (status) {
    case 'active':
      return { label: 'Active', severity: 'success' };
    case 'trialing':
      return { label: 'Trial', severity: 'info' };
    case 'past_due':
      return { label: 'Past Due', severity: 'warning' };
    case 'canceled':
      return { label: 'Canceled', severity: 'danger' };
    case 'incomplete':
      return { label: 'Incomplete', severity: 'warning' };
    default:
      return { label: 'Unknown', severity: 'info' };
  }
};
